import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GraduationCap, ChevronRight } from 'lucide-react';
import { useProCourseProgress } from '@/hooks/use-pro-formation';

/**
 * La bannière « prise en main » de la page d'accueil.
 *
 * Se retire d'elle-même : plus de parcours publié, ou parcours terminé, et
 * elle disparaît. Pas de bouton « fermer », donc rien à persister — c'est la
 * progression seule qui décide.
 */
export function CourseOnboardingBanner() {
  const { t } = useTranslation();
  const { percent, completedLessons, totalLessons, isComplete, isLoading, hasCourse, nextLessonId } =
    useProCourseProgress();

  if (isLoading || !hasCourse || isComplete) return null;

  const to = nextLessonId ? `/formation?lecon=${nextLessonId}` : '/formation';
  const isStart = completedLessons === 0;

  return (
    <Link
      to={to}
      className="pro-card pro-card-hoverable pro-fade-in relative flex items-center gap-3 overflow-hidden p-3 sm:gap-4 sm:p-4"
      style={{
        background: 'linear-gradient(120deg, #FFF6EF 0%, #FFEEE0 100%)',
        border: '1px solid var(--pro-primary-light)',
      }}
    >
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl sm:h-12 sm:w-12"
        style={{ background: 'var(--pro-primary)' }}
      >
        <GraduationCap className="h-5 w-5 text-white sm:h-6 sm:w-6" />
      </div>

      <div className="min-w-0 flex-1">
        <div
          className="truncate text-[13px] font-semibold sm:text-[14px]"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {isStart
            ? t('pro.formation.banner_start', 'Commencez par la prise en main')
            : t('pro.formation.banner_resume', 'Reprenez votre formation')}
        </div>
        <div className="mt-0.5 text-[12px]" style={{ color: 'var(--pro-text-muted)' }}>
          {t('pro.formation.banner_progress', '{{completed}} vidéo(s) sur {{total}}', {
            completed: completedLessons,
            total: totalLessons,
          })}
        </div>
        <div
          className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full"
          style={{ background: 'rgba(255,107,30,0.18)' }}
        >
          <div
            className="pro-progress-fill h-full rounded-full"
            style={{ width: `${Math.max(percent, 3)}%`, background: 'var(--pro-primary)' }}
          />
        </div>
      </div>

      <span
        className="flex h-9 shrink-0 items-center gap-1 rounded-xl px-3 text-[13px] font-semibold text-white"
        style={{ background: 'var(--pro-primary)' }}
      >
        <span className="hidden sm:inline">
          {isStart
            ? t('pro.formation.banner_cta_start', 'Démarrer')
            : t('pro.formation.banner_cta_resume', 'Continuer')}
        </span>
        <ChevronRight className="h-4 w-4 rtl:-scale-x-100" />
      </span>
    </Link>
  );
}
